// src/components/CameraScanner.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const SUPPORTED_FORMATS = [
  'code_128',
  'code_39',
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'itf',
  'codabar',
  'qr_code',
  'data_matrix',
  'aztec',
  'pdf417',
];

const hasBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

const CameraScanner = ({
  open,
  onClose,
  onDetected,
  continuous = false,
  title = 'Scan Barcode',
  scannedItems = [], // [{ id, name, qty }]
  onRemoveItem, // (id) => void
}) => {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const detectorRef = React.useRef(null);
  const [error, setError] = React.useState('');
  // Per-code cooldown map: code -> last accepted timestamp (ms)
  const lastAcceptedMapRef = React.useRef(new Map());

  const stopStream = React.useCallback(() => {
    try {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    } catch {}
    try {
      const stream = streamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch {}
    streamRef.current = null;
  }, []);

  const scanLoop = React.useCallback(async () => {
    if (!videoRef.current || !detectorRef.current) return;
    try {
      const detections = await detectorRef.current.detect(videoRef.current);
      if (detections && detections.length > 0) {
        const code = detections[0].rawValue || detections[0].rawValueStr || '';
        if (code) {
          const now = Date.now();
          const normalized = String(code).trim().toUpperCase();
          const lastTs = lastAcceptedMapRef.current.get(normalized) || 0;
          // Accept if first time or 3s have passed for this specific code
          if (now - lastTs >= 3000) {
            lastAcceptedMapRef.current.set(normalized, now);
            onDetected && onDetected(code);
            if (!continuous) {
              onClose && onClose();
              return;
            }
          }
        }
      }
    } catch (e) {
      // ignore transient errors
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [continuous, onClose, onDetected]);

  React.useEffect(() => {
    if (!open) return;
    let active = true;

    const init = async () => {
      setError('');
      lastAcceptedMapRef.current = new Map();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (!active) return;
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        if (!hasBarcodeDetector) {
          setError('Camera scanning is not supported in this browser. Please use Chrome/Edge or an updated mobile browser.');
          return;
        }
        detectorRef.current = new window.BarcodeDetector({ formats: SUPPORTED_FORMATS });
        rafRef.current = requestAnimationFrame(scanLoop);
      } catch (e) {
        setError(e?.message || 'Failed to access camera');
      }
    };

    init();
    return () => {
      active = false;
      stopStream();
    };
  }, [open, scanLoop, stopStream]);

  const handleClose = () => {
    stopStream();
    onClose && onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{title}</span>
        <IconButton size="small" onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Typography color="error" sx={{ my: 2 }}>{error}</Typography>
        ) : (
          <>
            <Box sx={{ position: 'relative', width: '100%', pt: '56.25%', bgcolor: 'black', borderRadius: 1, overflow: 'hidden' }}>
              <video
                ref={videoRef}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                muted
                playsInline
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '10%',
                  left: '10%',
                  right: '10%',
                  bottom: '10%',
                  border: '2px dashed rgba(255,255,255,0.7)',
                  borderRadius: 1,
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Scanned Items
              </Typography>
              <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {Array.isArray(scannedItems) && scannedItems.length > 0 ? (
                  scannedItems.map((it) => (
                    <Box key={it.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
                      <Box sx={{ minWidth: 0, pr: 1 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{it.name}</Typography>
                        <Typography variant="caption" color="text.secondary">Qty: {it.qty ?? it.quantity ?? 1}</Typography>
                      </Box>
                      {onRemoveItem && (
                        <Button size="small" color="error" variant="outlined" onClick={() => onRemoveItem(it.id)}>
                          Remove
                        </Button>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 1.5 }}>
                    No items yet. Scan a barcode to add.
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraScanner;
